import { useEffect, useState } from "react";
import { Loader2, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Simple admin dashboard for all paid coloring-book purchases.
 * Includes buyer email, book, date, amount, and one-click refund via Stripe.
 */
export default function AdminPurchases() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/purchases")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const refund = async (row) => {
    const ok = window.confirm(
      `Refund $${(row.amount || 0) / 100} to ${row.email || "(unknown email)"} for "${row.book_title}"?\n\nThis also removes their access.`,
    );
    if (!ok) return;
    setRefunding(row.session_id);
    try {
      await api.post(`/admin/purchases/${row.session_id}/refund`);
      toast.success("Refund issued and access removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't process refund.");
    } finally {
      setRefunding(null);
    }
  };

  const totalCents = rows
    .filter((r) => r.kind !== "refunded")
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalUsd = (totalCents / 100).toFixed(2);

  return (
    <div className="max-w-5xl" data-testid="admin-purchases-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-fredoka text-3xl" style={{ color: "#2D3055" }}>Purchases</h1>
          <p className="font-nunito text-sm mt-1" style={{ color: "#7E82A1" }}>
            Every coloring-ebook sale in one warm little table.
          </p>
        </div>
        <div className="text-right">
          <p className="font-caveat text-2xl" style={{ color: "#FF6B9D" }}>lifetime revenue</p>
          <p className="font-fredoka text-3xl" style={{ color: "#2D3055" }}>${totalUsd}</p>
        </div>
      </div>

      {loading ? (
        <p className="font-nunito" style={{ color: "#7E82A1" }}>Gathering the receipts…</p>
      ) : rows.length === 0 ? (
        <div className="paper-card p-8 text-center" data-testid="admin-purchases-empty">
          <p className="font-fredoka text-xl" style={{ color: "#2D3055" }}>No purchases yet.</p>
          <p className="mt-2 font-nunito" style={{ color: "#4A4E6D" }}>
            Every sale will show up here in real time.
          </p>
        </div>
      ) : (
        <div className="rounded-3xl overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid #F0EBFF" }}>
          <table className="w-full" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#FFF3F8" }}>
                {["Date", "Buyer", "Book", "Amount", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-fredoka text-xs uppercase tracking-wide"
                      style={{ color: "#7E82A1" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const when = r.purchased_at ? new Date(r.purchased_at).toLocaleDateString(undefined, {
                  month: "short", day: "numeric", year: "numeric",
                }) : "—";
                const amount = ((r.amount || 0) / 100).toFixed(2);
                const isRefunded = r.kind === "refunded";
                return (
                  <tr key={r.session_id || r.id} data-testid={`admin-purchase-${r.session_id}`}
                      style={{ borderTop: "1px solid #F0EBFF", opacity: isRefunded ? 0.55 : 1 }}>
                    <td className="px-4 py-3 font-nunito text-sm" style={{ color: "#2D3055" }}>{when}</td>
                    <td className="px-4 py-3 font-nunito text-sm" style={{ color: "#2D3055" }}>
                      <div>{r.name || "(no name)"}</div>
                      <div className="text-xs" style={{ color: "#7E82A1" }}>{r.email || "(no email)"}</div>
                    </td>
                    <td className="px-4 py-3 font-nunito text-sm" style={{ color: "#2D3055" }}>{r.book_title}</td>
                    <td className="px-4 py-3 font-fredoka" style={{ color: "#2D3055" }}>
                      ${amount} <span className="text-xs" style={{ color: "#7E82A1" }}>{r.currency?.toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!isRefunded && r.session_id && (
                        <button
                          type="button"
                          onClick={() => refund(r)}
                          disabled={refunding === r.session_id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-fredoka text-xs transition-transform hover:-translate-y-0.5"
                          style={{
                            background: "#FFFFFF",
                            color: "#D6407E",
                            border: "1.5px solid #F6ACC7",
                          }}
                          data-testid={`admin-purchase-refund-${r.session_id}`}
                        >
                          {refunding === r.session_id
                            ? (<><Loader2 size={12} className="animate-spin" /> Refunding…</>)
                            : (<><RotateCcw size={12} /> Refund</>)
                          }
                        </button>
                      )}
                      {isRefunded && (
                        <span className="inline-flex items-center gap-1 font-caveat text-sm"
                              style={{ color: "#7E82A1" }}>
                          <ShieldAlert size={12} /> refunded
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
